import { useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { MdFilterList } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import { categoryAndAutherProps } from "~/interface/article/article";
import { cn } from "~/lib/utils";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { SheetClose } from "../ui/sheet";

interface BlogFilterMobileProps {
  categoryArray: categoryAndAutherProps[];
  authorArray: categoryAndAutherProps[];
  selectedCategoryId: number | null;
  selectedAuthorId: number | null;
  setIsSheetOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FilterCategory {
  key: string;
  title: string;
  data: categoryAndAutherProps[];
  tempSelected: string;
  setTempSelected: React.Dispatch<React.SetStateAction<string>>;
}

const BlogFilterMobile = ({ categoryArray, authorArray, selectedCategoryId, selectedAuthorId, setIsSheetOpen }: BlogFilterMobileProps) => {
  const { t } = useTranslation();
  const { medium, normalInter } = useFontStyle();
  const [, setSearchParams] = useSearchParams();

  // Find currently selected items
  const currentSelectedCategory = selectedCategoryId ? categoryArray.find((cat) => cat.id === selectedCategoryId) : null;
  const currentSelectedAuthor = selectedAuthorId ? authorArray.find((aut) => aut.id === selectedAuthorId) : null;

  // Temporary state for mobile selections - using string values for radio groups
  const [tempSelectedCategory, setTempSelectedCategory] = useState<string>(currentSelectedCategory ? currentSelectedCategory.id.toString() : "");
  const [tempSelectedAuthor, setTempSelectedAuthor] = useState<string>(currentSelectedAuthor ? currentSelectedAuthor.id.toString() : "");

  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});
  const [activeCategory, setActiveCategory] = useState<string>("category");

  // Sync temp state when component mounts or selections change
  useEffect(() => {
    setTempSelectedCategory(currentSelectedCategory ? currentSelectedCategory.id.toString() : "");
    setTempSelectedAuthor(currentSelectedAuthor ? currentSelectedAuthor.id.toString() : "");
    setActiveCategory("category");
  }, [selectedCategoryId, selectedAuthorId, currentSelectedCategory, currentSelectedAuthor]);

  const categories: FilterCategory[] = [
    {
      key: "category",
      title: t("blog.category"),
      data: categoryArray,
      tempSelected: tempSelectedCategory,
      setTempSelected: setTempSelectedCategory,
    },
    {
      key: "author",
      title: t("blog.author"),
      data: authorArray,
      tempSelected: tempSelectedAuthor,
      setTempSelected: setTempSelectedAuthor,
    },
  ];

  const activeCategoryData = categories.find((cat) => cat.key === activeCategory);

  const handleToggleItem = (itemId: string) => {
    if (!activeCategoryData) return;

    if (activeCategory === "category") {
      setTempSelectedCategory(itemId);
    } else if (activeCategory === "author") {
      setTempSelectedAuthor(itemId);
    }
  };

  const getFilteredItems = () => {
    if (!activeCategoryData) return [];

    const searchTerm = searchTerms[activeCategory]?.toLowerCase() || "";
    return activeCategoryData.data.filter((item) => item.text && item.text.toLowerCase().includes(searchTerm));
  };

  const handleClearAll = () => {
    setTempSelectedCategory("");
    setTempSelectedAuthor("");
    setSearchTerms({});
  };

  const handleApplyFilters = () => {
    const newParams = new URLSearchParams();

    if (tempSelectedCategory) {
      const selectedCategory = categoryArray.find((cat) => cat.id.toString() === tempSelectedCategory);
      if (selectedCategory?.text) {
        newParams.set("category", selectedCategory.text.toLowerCase());
      }
    }
    if (tempSelectedAuthor) {
      const selectedAuthor = authorArray.find((aut) => aut.id.toString() === tempSelectedAuthor);
      if (selectedAuthor?.text) {
        newParams.set("author", selectedAuthor.text.toLowerCase());
      }
    }

    setSearchParams(newParams, { replace: true });
    setIsSheetOpen(false);
  };

  const getTotalSelectedCount = () => {
    return (tempSelectedCategory ? 1 : 0) + (tempSelectedAuthor ? 1 : 0);
  };

  return (
    <div className="w-full h-full flex flex-col justify-between">
      {/* Header */}
      <div className="flex flex-col h-[90%]">
        <div className="px-4 py-3 flex justify-between items-center border-b border-borderPrimary bg-primary z-10">
          <h2 className={cn("text-16 text-textColor font-medium", medium)}>{t("common.filter")}</h2>
          <SheetClose className="flex">
            <IoClose size={24} className="text-textColor cursor-pointer" />
          </SheetClose>
        </div>

        <div className="w-full overflow-hidden flex h-full">
          {/* Categories Sidebar */}
          <div className="w-[35%] h-full border-r border-borderPrimary bg-cardBg">
            {categories.map(
              (category) =>
                category.data.length > 0 && (
                  <button
                    key={category.key}
                    onClick={() => {
                      setActiveCategory(category.key);
                      setSearchTerms((prev) => ({ ...prev, [category.key]: prev[category.key] || "" }));
                    }}
                    className={cn(
                      "w-full px-4 py-3 text-left transition-all",
                      activeCategory === category.key
                        ? "bg-primary border-l-4 border-l-textHeadingColor "
                        : "bg-transparent hover:bg-opacity-70 border-l-4 border-l-transparent",
                    )}
                  >
                    <p
                      className={cn(
                        "text-[14px] text-textColor font-medium",
                        activeCategory === category.key && "text-textHeadingColor",
                        normalInter,
                      )}
                    >
                      {category.title}
                    </p>
                  </button>
                ),
            )}
          </div>

          <div className="flex-1 flex flex-col justify-between h-full">
            {activeCategoryData && activeCategoryData.data.length > 0 && (
              <>
                {/* Search Input */}
                <div className="w-full p-1.5">
                  <div className="w-full flex items-center border border-borderPrimary bg-cardBg p-2 rounded-lg">
                    <Search size={16} className="text-textSecondaryColor" />
                    <input
                      type="text"
                      placeholder={`Search ${activeCategoryData.title}`}
                      value={searchTerms[activeCategory] || ""}
                      onChange={(e) => setSearchTerms((prev) => ({ ...prev, [activeCategory]: e.target.value }))}
                      className={cn(
                        "w-full pl-2 bg-transparent text-textSecondaryColor text-13 placeholder:text-textSecondaryColor outline-none",
                        normalInter,
                      )}
                    />
                  </div>
                </div>

                {/* Items List */}
                <div className="h-[100%] overflow-y-auto">
                  {getFilteredItems().length > 0 ? (
                    <RadioGroup
                      value={activeCategory === "category" ? tempSelectedCategory : tempSelectedAuthor}
                      onValueChange={handleToggleItem}
                      className="flex-1 flex flex-col justify-start gap-0"
                    >
                      {getFilteredItems().map((item) => (
                        <div key={item.id} className="flex items-center gap-3 p-2 rounded cursor-pointer transition-colors">
                          <RadioGroupItem
                            id={`radio-${activeCategory}-${item.id}`}
                            value={item.id.toString()}
                            className="border-borderPrimary cursor-pointer w-4 h-4"
                          />
                          <label
                            htmlFor={`radio-${activeCategory}-${item.id}`}
                            className={cn("text-base text-textColor flex-1 cursor-pointer line-clamp-2", normalInter)}
                            title={item.text}
                          >
                            {item.text}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className={cn("text-base text-textColor text-opacity-50", normalInter)}>No results found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Apply & Clear All Buttons */}
      <div className="flex-1 h-[10%] px-4 py-3 border-t border-borderPrimary bg-primary flex gap-3 justify-center items-center">
        <button
          onClick={handleClearAll}
          className={cn(
            "flex-1 py-2.5 rounded-full bg-cardBg !text-textHeadingColor text-base font-medium transition-opacity flex justify-center items-center",
            normalInter,
            "hover:bg-opacity-60",
          )}
        >
          {t("common.clear_all")}
        </button>
        <div
          onClick={handleApplyFilters}
          className={cn(
            "flex-1 py-2.5 rounded-full bg-buttonColor !text-white text-14 font-medium transition-opacity cursor-pointer flex justify-center items-center gap-2",
            normalInter,
            "hover:bg-opacity-80",
          )}
        >
          <button className="flex justify-center items-center gap-2">
            <MdFilterList size={18} />
            {t("common.apply")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogFilterMobile;
